import React, { useMemo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  PLATFORM_LABEL, relDate,
  SectionHeader, KpiColumn,
} from './sections';
import type { PlatformInfo, UploadItem, SponsorDeal, Tier } from './sections';
import SponsorLedger, { type AddSponsorRequest } from './SponsorLedger';
import MerchSection, { type MerchOwnerHandlers } from '../creator/MerchSection';
import ThreeStepsCard from './ThreeStepsCard';
import ChannelFolio from './ChannelFolio';
import YouTubeAnalytics from './YouTubeAnalytics';
import SponsorLinks from './SponsorLinks';
import PageHeader from '../ui/PageHeader';
import type { MerchProduct, OutreachCard } from '../../services/dataService';
import { DealsGlance } from './DeskGlance';

/**
 * Owner dashboard — the creator's home, and the one place their channel is
 * managed: channels and uploads, sponsors (SponsorLedger — the only sponsor
 * list, since the Sponsors tab folded in), the shop, and where every deal
 * stands. It composes the shared creator-view sections
 * (./sections); the public looked-up creator view (CreatorProfile) composes
 * the SAME sections read-only.
 *
 * The desk: one lamp, a few named objects, one bear (the hero pin). Every
 * section below the ledger is a sheet; rows on a sheet are ruled, never
 * boxed; numbers are counts; a decision already made is a stamp; the one
 * blue slab in each sheet is the thing to do there; an empty sheet is ruled
 * paper with one pencilled line.
 */

// ═══════════════════════════════════════════════════════════════════════
export interface OwnerDashboardProps {
  /** Link a handle-connected platform (TikTok/Twitch) — kicks the async
   *  scrape for that account and pins it to the workspace. */
  onLinkByHandle?: (platform: string, handle: string) => Promise<void> | void;
  /** Unpin a handle-linked platform (typo recovery — the tile falls back
   *  to the "+ TikTok" tag). */
  onUnlinkHandle?: (platform: string) => void;
  identity: { name: string; handle: string; avatarUrl?: string | null; tier?: Tier };
  platforms: PlatformInfo[];
  uploads: UploadItem[];
  lastScraped?: string | null;
  refreshing?: boolean;
  refreshDisabledReason?: string | null;
  onRefresh?: () => void;
  onRefreshPlatform?: (platform: string, handle: string) => void;
  onResearchUpdated?: () => void;
  refreshingPlatforms?: Set<string>;
  onConnect?: (platformId: string) => void;
  deals: SponsorDeal[];
  savingSponsor?: boolean;
  onAddSponsor?: (input: AddSponsorRequest) => Promise<void>;
  /** No YouTube connection: a sponsor is filed by its code and store (see SponsorLedger). */
  sponsorNeedsCode?: boolean;
  /** The codes half of the sponsor list is still being read (the sponsorships come with `loading`). */
  sponsorsLoading?: boolean;
  /** Scraped shop products + fallback store links for the owner's own shop. */
  merchProducts?: MerchProduct[];
  merchFallbackShops?: { domain: string; url: string }[];
  /** Owner-edit callbacks for the Shop section — connect/change the link and
   *  edit products. Present only when a creator identity resolves. */
  merchOwner?: MerchOwnerHandlers;
  loading?: boolean;
  /** Kept for the page's call site; the ledger no longer links out, so the
   *  gates are drawn on the destinations themselves. */
  hasPitchAccess?: boolean;
  hasProspectAccess?: boolean;
  /** The Deals pipeline for the Deals-at-a-glance card; null while it loads. */
  pipeline?: OutreachCard[] | null;
  pipelineError?: boolean;
  /** The YouTube sheet's Studio numbers. Left out, it is the live block, which reads the
   *  signed-in creator's own channel; the dev desk preview hands in fixtures. */
  youtubeAnalytics?: React.ReactNode;
  /** The sponsor-links sheet. Left out, it is the live list of the signed-in creator's Bearri
   *  links; the dev desk preview hands in the presentational view with fixtures. */
  sponsorLinks?: React.ReactNode;
}

const OwnerDashboard: React.FC<OwnerDashboardProps> = ({
  identity, platforms, uploads, lastScraped, refreshing, refreshDisabledReason, onLinkByHandle, onUnlinkHandle,
  onRefresh, onRefreshPlatform, onResearchUpdated, refreshingPlatforms, onConnect, deals,
  savingSponsor, onAddSponsor, sponsorNeedsCode = false, sponsorsLoading = false, loading = false,
  merchProducts = [], merchFallbackShops = [], merchOwner, pipeline = null, pipelineError = false, youtubeAnalytics, sponsorLinks,
}) => {
  const { hash } = useLocation();
  // The old Sponsors tab lands here as /app/dashboard#sponsors: once the page has its data, bring
  // the section into view (the router does not scroll to a hash on its own).
  const scrolledToHash = useRef(false);
  useEffect(() => {
    if (loading || scrolledToHash.current || !hash) return;
    scrolledToHash.current = true;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash, loading]);

  const connected = platforms.filter(p => p.connected || p.saved);
  // Which YouTube videos Bearri found sponsors in (its own checks of the uploads) — the Studio
  // block colours each video's first week by it.
  const youtubeSponsors = useMemo(() => Object.fromEntries(uploads.filter(u => u.platform === 'youtube')
    .map(u => [u.id, { brands: u.brands.map(b => b.name), checked: u.scraped }])), [uploads]);
  // Every platform with a place on the channels shelf: a sheet if it is there, a slot if it can be.
  const shelf = platforms.filter(p => p.connected || p.saved || p.connectable);
  const connectedLabels = connected.map(p => PLATFORM_LABEL[p.id] || p.label).join(' · ');
  const totalReach = useMemo(() => {
    const vals = connected.map(p => p.followers).filter((n): n is number => typeof n === 'number');
    return vals.length ? vals.reduce((s, n) => s + n, 0) : null;
  }, [connected]);
  const dealsWithCode = deals.filter(d => d.code);

  return (
    <div className="w-full">
      {/* ── Hero: the page's one bear is the channels pin; it only comes alive for page-level work ── */}
      <PageHeader title={`Welcome back, ${identity.name}.`}
        subtitle={connectedLabels || undefined}
        breadcrumbs={[]} badge="channels" busy={loading} eyebrow="Dashboard" chapter="keep" />

      {/* New-creator activation — self-hides once all three steps are done. */}
      <ThreeStepsCard />

      {/* ── The ledger: your channel at a glance — audience and sponsors — on ONE sheet ── */}
      <div className="br-sheet br-ledger br-rise mt-[22px]">
        <KpiColumn loading={loading} label="Total reach" value={totalReach}
          sub={<span>{connected.length} saved profile{connected.length === 1 ? '' : 's'}</span>} />
        <KpiColumn loading={loading || sponsorsLoading} label="Sponsors" value={deals.length}
          sub={<span>{dealsWithCode.length} with a discount code</span>} />
      </div>

      {/* ── Your channels: straight under the numbers, because they are where every number comes from ── */}
      <section>
        <SectionHeader doodle="camera" tone="blue"
          title="Your channels" subtitle="Your content and the sponsors in it. Each account keeps its own refresh schedule."
          right={onRefresh ? (
            <div className="flex flex-col items-end gap-1.5">
              <button type="button" onClick={onRefresh} disabled={refreshing || !!refreshDisabledReason}
                className="br-slab br-slab--blue br-slab--sm">
                {refreshing ? 'Checking channels…' : 'Check recent posts'}
              </button>
              <span className="br-caption">{refreshDisabledReason || (lastScraped ? `Data saved ${relDate(lastScraped)}` : 'No saved-data date')}</span>
            </div>
          ) : undefined} />

        <p className="br-small mb-4">Checks cover a recent-post window, not every historical post.
          TikTok and Twitch currently show linked profiles only.</p>
        {/* One folder tab per platform, in one order; the open one is joined to the sheet under
            the row, which is that platform's desk (its refresh, what Bearri can read there, its
            recent uploads). Back from a platform's sign-in, that platform's tab opens first. */}
        <ChannelFolio platforms={shelf} uploads={uploads} loading={loading}
          initialPlatform={hash === '#youtube-analytics' ? 'youtube'
            : (hash.match(/^#channel-sheet-(youtube|instagram|tiktok|twitch)$/)?.[1] as PlatformInfo['id'] | undefined) ?? null}
          analytics={{ youtube: youtubeAnalytics ?? <YouTubeAnalytics sponsors={youtubeSponsors} /> }}
          refreshing={refreshing} refreshingPlatforms={refreshingPlatforms} onRefreshPlatform={onRefreshPlatform}
          onResearchUpdated={onResearchUpdated} onConnect={onConnect} onLinkByHandle={onLinkByHandle} onUnlinkHandle={onUnlinkHandle} />
      </section>

      {/* ── Your sponsors: the one list (the Sponsors tab folded in here) ── */}
      <section id="sponsors" className="scroll-mt-40">
        <SectionHeader doodle="megaphone" tone="ochre" title="Your sponsors" />
        <SponsorLedger deals={deals} loading={loading || sponsorsLoading} uploads={uploads} onAddSponsor={onAddSponsor}
          savingSponsor={savingSponsor} needsCode={sponsorNeedsCode} />
      </section>

      {/* ── Your sponsor links: Bearri links that forward to a sponsor's exact URL, with their
          anonymous click counts. Right under the sponsors they are for. ── */}
      <section id="links" className="scroll-mt-40">
        <SectionHeader doodle="clip" tone="plum" title="Your sponsor links"
          subtitle="Bearri links that forward to the brand's exact URL. Clicks are counted anonymously — not sales." />
        {sponsorLinks ?? <SponsorLinks pipeline={pipeline} />}
      </section>

      {/* ── Your deals: the same brands, further along — so it lies right under the sponsors.
          Bearri itself is the tab in the header; this is where each conversation stands. ── */}
      <section id="deals" aria-label="Your deals">
        <SectionHeader doodle="envelope" tone="rose" title="Your deals" subtitle="Where each conversation stands."
          right={<Link to="/app/bearri" className="br-inkline">Open in Bearri</Link>} />
        <DealsGlance cards={pipeline} error={pipelineError} />
      </section>

      {/* ── Shop & Merch ── at the BOTTOM (the catalog can be large). Owner-
          editable here: connect/change the shop link and correct products. The
          public creator profile shows the same section read-only. */}
      {merchOwner && (
        <MerchSection
          firstName={identity.name}
          merchProducts={merchProducts}
          merchFallbackShops={merchFallbackShops}
          owner={merchOwner}
        />
      )}

    </div>
  );
};

export default OwnerDashboard;
